
import "./index.css";
import { useEffect, useRef } from 'react';
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';

Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
  Legend
);

function hexToRgb(hex) {
  const match = /^#?([0-9a-f]{6})$/i.exec((hex || '').trim());
  if (!match) return null;
  const int = parseInt(match[1], 16);
  return `${(int >> 16) & 255}, ${(int >> 8) & 255}, ${int & 255}`;
}

function formatBRL(value) {
  try {
    return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value || 0));
  } catch {
    return String(value || 0);
  }
}

export default function RevenueChart({ labels = [], data = [], period, total }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    console.log(`[RevenueChart] Criando gráfico para período: ${period}, total: ${total}, data:${data}`);

    // Cor do tema (useTheme.js)
    const rootStyles = getComputedStyle(document.documentElement);
    const purple = rootStyles.getPropertyValue('--purple').trim() || '#8b5cf6';
    const purpleRgb = hexToRgb(purple) || '139, 92, 246';

    // Gradient similar to template
    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    gradient.addColorStop(0, `rgba(${purpleRgb}, 0.4)`);
    gradient.addColorStop(0.5, `rgba(${purpleRgb}, 0.2)`);
    gradient.addColorStop(1, `rgba(${purpleRgb}, 0.0)`);

    // Clean previous chart
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    let normalizedData = (data || []).map(n => Number(n || 0));

    if (normalizedData.length === 0) {
      normalizedData = [0, 0];
    }
    let normalizedLabels = labels && labels.length > 0 ? labels : ['', ''];



    try {
      chartRef.current = new Chart(ctx, {
        type: 'line',
        data: {
          labels: normalizedLabels,
          datasets: [
            {
              label: 'Faturamento',
              data: normalizedData,
              borderColor: purple,
              backgroundColor: gradient,
              fill: true,
              tension: 0.4,
              borderWidth: 2,
              pointBackgroundColor: purple,
              pointBorderColor: '#ffffff',
              pointBorderWidth: 2,
              pointRadius: 4,
              pointHoverRadius: 6,

              pointStyle: 'circle',
              pointBackgroundColor: purple,   // 👈 FUNDO DA BOLA
              pointBorderColor: purple,       // 👈 BORDA DA BOLA
              pointBorderWidth: 0,              // 👈 REMOVE BORDA
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          plugins: {
            legend: {
              display: false,
              position: 'top',
              labels: { color: '#9ca3af' },
            },

            tooltip: {
              backgroundColor: '#0d1014',
              titleColor: '#e5e7eb',
              bodyColor: '#e5e7eb',
              borderColor: 'rgba(148, 163, 184, 0.2)',
              borderWidth: 1,
              padding: 12,
              displayColors: true,
              usePointStyle: true,

              callbacks: {
                label: function (context) {
                  const y = context.parsed?.y ?? 0;
                  return 'Faturamento: R$ ' + formatBRL(y);
                },
              },
            }
          },


          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                color: '#9ca3af',
                callback: function (value) {
                  return 'R$ ' + formatBRL(value);
                },
              },
              grid: {
                display: false,
                drawBorder: false,
              },
            },
            x: {
              ticks: { color: '#9ca3af' },
              grid: {
                display: false,
                drawBorder: false,
              },
            },
          },
        },
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[RevenueChart] Error creating chart:', err);
    }

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [labels, data]);

  return (
    <div className="chart-card large">
      <div className="chart-header">
        <div className="chart-legend">
          <span className="legend-item">
            <span className="legend-dot purple"></span>
            Receita
          </span>
        </div>
      </div>
      <div className="chart-container" style={{ height: 420 }}>
        <canvas ref={canvasRef} />
      </div>
    </div>
  );
}
